import React, { useState } from 'react';
import { Button, Card, Panel, Table } from '@/components/Bicester';
import { Form, FormField } from '@/components/formik-rsuite';
import { usePersistFn, useRequest } from 'ahooks';
import { FormikProvider, useFormik } from 'formik';
import { Alert, IconButton } from 'rsuite';
import useTable from '@/hooks/useTable';
import { Cell, EditableCell, OperationCell } from '@/components/Bicester/Table/Cells';
import useContentHeight from '@/hooks/useContentHeight';
import PageLoader from '@/components/shared/PageLoader';
import _ from 'lodash';
import { fetchList, fetchAddUpdate, fetchDel } from '@/services/subscribe/margin';
import { defaultMarginRecordInfo, marginSchema, optionPickerData } from './constants';
import { DEFAULT_TABLE_VALUE } from '@/constants/config';
import { SERVICE_STATUS } from '@/services/constants';
import { defaultMarginInfo } from './constants';
import { EffectiveTimeCell } from './Cell';
import { BICESTER_FULL_DATE_HOURS_MINUTES, FULL_DATE_HOURS_MINUTES } from '@/constants/dateFormat';
import { endOfDay, isBefore, subMinutes } from 'date-fns';
import dayjs from 'dayjs';
import { onChangeRowData } from '../utils';
import { LAYOUT_CONFIG } from '@/constants/layoutConfig';
import Image from '@/components/Bicester/Image';
import { DateRangePicker } from 'rsuite5';
import { PopoverWrapper } from '@/components/Bicester/PopoverWrapper';

const Margin = () => {
  const [data, setData] = useState([]);

  const { run: addUpdateMargin, loading: addUpdateMarginLoading } = useRequest<any>(
    fetchAddUpdate,
    { manual: true }
  );
  const { run: delMargin, loading: delMarginLoading } = useRequest<any>(fetchDel, { manual: true });

  const {
    run: getList,
    data: { total, current_page },
    loading: getListLoading,
  } = useRequest<typeof DEFAULT_TABLE_VALUE>(fetchList, {
    initialData: DEFAULT_TABLE_VALUE,
    onSuccess: (res) => setData(res.content_list),
  });

  const { onChange, onSearch, onRefresh } = useTable(getList);

  const formik = useFormik({
    initialValues: defaultMarginInfo,
    validationSchema: marginSchema,
    enableReinitialize: true,
    onSubmit: (values) =>
      addUpdateMargin(values).then((res) => {
        if (res?.status === SERVICE_STATUS.SUCCESS) {
          Alert.success('新增成功');
          formik.resetForm();
          onRefresh();
        }
      }),
  });
  const recordFormik = useFormik({
    initialValues: defaultMarginRecordInfo,
    enableReinitialize: true,
    onSubmit(values, actions) {
      const params = Object.assign({}, _.omit(values, 'time_range'), {
        start_time: values?.time_range?.[0] || '',
        end_time: values?.time_range?.[1] || '',
      });
      onSearch(params);
    },
  });

  const operations = usePersistFn((rowData) => {
    const { isEdit, id, status, end_time } = rowData;
    if (status === 0) return [{ name: '已取消', readonly: true }];

    const show = !isBefore(new Date(end_time), new Date()); // 过去时间的设置不能操作
    const name = isEdit ? '保存' : '编辑';
    const onClick = async () => {
      if (isEdit) {
        const { status } = await addUpdateMargin(
          _.pick(rowData, ['id', 'option_id', 'start_time', 'end_time', 'number'])
        );
        if (status === SERVICE_STATUS.SUCCESS) {
          Alert.success('保存成功');
          onRefresh();
        } else {
          return;
        }
      }
      onChangeRowData(id, { isEdit: !isEdit }, setData);
    };

    const handleDelete = async () => {
      // delete
      const { status } = await delMargin({ id: rowData?.id });
      if (status === SERVICE_STATUS.SUCCESS) {
        Alert.success('取消成功');
        onRefresh();
      }
    };

    return [
      { show, name, onClick },
      { show, name: '取消', onClick: handleDelete },
    ];
  });

  return (
    <>
      {(addUpdateMarginLoading || delMarginLoading) && <PageLoader />}
      <Panel title="新增余量设置" dividerHeight={15} leftShadow>
        <FormikProvider value={formik}>
          <Form>
            <Card smDivider>
              <div className="flex items-center">
                <Card.Picker label="设置项目" formKey="option_id" pickerData={optionPickerData} />
                <Card.Input label="数量限制" formKey="number" inputType="number" />
                <Card.DatePicker
                  label="生效开始时间"
                  formKey="start_time"
                  onChange={(value) => {
                    formik.setFieldValue(
                      'end_time',
                      dayjs(endOfDay(new Date(value))).format(BICESTER_FULL_DATE_HOURS_MINUTES)
                    );
                  }}
                  onClean={() => formik.setFieldValue('end_time', '')}
                  format={BICESTER_FULL_DATE_HOURS_MINUTES}
                  readOnlyFormat={BICESTER_FULL_DATE_HOURS_MINUTES}
                  disabledDate={(date) => isBefore(date, subMinutes(new Date(), 1))}
                />
                <Card.DatePicker
                  label="生效结束时间"
                  formKey="end_time"
                  format={BICESTER_FULL_DATE_HOURS_MINUTES}
                  readOnlyFormat={BICESTER_FULL_DATE_HOURS_MINUTES}
                  disabledDate={(date) =>
                    isBefore(
                      date,
                      formik.values?.start_time
                        ? new Date(formik.values?.start_time)
                        : subMinutes(new Date(), 1)
                    )
                  }
                />
                <div className="flex-1 flex justify-end max-w-[200px]">
                  <Button type="submit" className="w-[135px] h-[30px] p-0">
                    确认新增
                  </Button>
                </div>
              </div>
              <div className="flex flex-[4]">
                <Card.Input
                  label="设置原因"
                  formKey="reason"
                  inputType="textarea"
                  placeholder="最大限制100字符"
                  maxLength={100}
                  labelWeight={1}
                  contentWeight={7}
                />
                <div className="flex-1 max-w-[200px]"></div>
              </div>
              <div className="mt-[20px] ml-[18px] text-[14px] text-[#999999]">
                <p className="text-[14px] m-0 leading-[17px]">
                  注意： 代客泊车余量默认 上海130位/天，苏州40位/天
                </p>
                <p className="text-[14px] m-0 leading-[17px]">
                  贵宾休息室余量默认 上海周一至周五8组/每2小时段，周末&节假日15组/每2小时段，苏州13组/每2小时段
                </p>
                <p className="text-[14px] m-0 leading-[17px]">
                  导购大使余量默认 上海3组/每3小时段，苏州3组/每3小时段
                </p>
              </div>
            </Card>
          </Form>
        </FormikProvider>
      </Panel>
      <Panel
        title="历史设置记录"
        leftShadow
        style={{
          height:
            'max(calc(100vh - var(--headerHeight) - var(--navBarHeight) - var(--toolBarHeight) - 338px), auto)',
        }}
        subTitle={`总计 ${total || 0} 条`}
        toolbar={
          <div className="flex justify-end">
            <FormikProvider value={recordFormik}>
              <Form style={{ width: 'calc(75% + 150px)' }}>
                <Card smDivider horizontal>
                  <div className="flex-[1.5] flex items-center">
                    <FormField
                      accepter={DateRangePicker}
                      label="生效时间"
                      name="time_range"
                      labelStyle={{
                        flex: 0.5,
                        marginLeft: 0,
                        marginRight: 15,
                        whiteSpace: 'nowrap',
                      }}
                      format="yyyy-MM-dd hh:mm"
                      onChange={(val) => {
                        console.log(val);
                        val?.length === 2
                          ? recordFormik.setFieldValue('time_range', [
                              dayjs(val[0]).format(FULL_DATE_HOURS_MINUTES),
                              dayjs(val[1]).format(FULL_DATE_HOURS_MINUTES),
                            ])
                          : [];
                      }}
                      onClean={() => recordFormik.setFieldValue('time_range', [])}
                      ranges={[]}
                      showMeridian={false}
                      inline
                    />
                  </div>
                  <Card.Picker
                    label="设置项目"
                    formKey="option_id"
                    pickerData={optionPickerData}
                    labelWeight={1}
                    contentWeight={2}
                  />
                  <Card.Picker
                    label="是否取消"
                    formKey="is_delete"
                    pickerData={[
                      { label: '全部', value: 0 },
                      { label: '是', value: 1 },
                      { label: '否', value: 2 },
                    ]}
                    labelWeight={1}
                    contentWeight={2}
                  />
                  <IconButton
                    type="submit"
                    className="flex items-center w-[30px] ml-[10px]"
                    style={{ height: 47 }}
                  >
                    <Image src="/resources/images/match.svg" />
                  </IconButton>
                </Card>
              </Form>
            </FormikProvider>
          </div>
        }
      >
        <Table
          style={{
            height: `max(${
              useContentHeight({
                withToolBar: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withPanelHeader: true,
                withTablePagination: true,
              }) - 338
            }px, ${
              LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT +
              LAYOUT_CONFIG.TABLE_PAGINATION_WITH_MARGIN_HEIGHT
            }px})`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          loading={getListLoading}
          pageInfo={{ page: current_page, total: total }}
          data={data}
          columns={[
            {
              columnProps: { flexGrow: 1 },
              header: '创建时间',
              cell: <Cell dataKey="created_at" />,
            },
            {
              columnProps: { flexGrow: 1 },
              header: '创建人',
              cell: <Cell dataKey="creator_name" />,
            },
            {
              columnProps: { flexGrow: 1 },
              header: '设置项目',
              cell: <Cell dataKey="option" />,
            },
            {
              columnProps: { flexGrow: 2.5 },
              header: '生效时间',
              cell: <EffectiveTimeCell setState={setData} />,
            },
            {
              columnProps: { flexGrow: 1 },
              header: '数量限制',
              cell: (
                <EditableCell
                  editComponent="inputNumber"
                  dataKey="number"
                  setState={setData}
                  min={1}
                />
              ),
            },
            {
              columnProps: {
                flexGrow: 2,
              },
              header: '设置原因',
              cell: (
                <Cell>
                  {(rowData) => (
                    <PopoverWrapper popover={{ content: rowData?.reason }}>
                      <div className="rs-table-cell-content px-0">{rowData?.reason}</div>
                    </PopoverWrapper>
                  )}
                </Cell>
              ),
            },
            {
              columnProps: { flexGrow: 1 },
              header: '操作',
              cell: <OperationCell operations={operations} />,
            },
          ]}
          onChange={onChange}
        >
          <Table.Body
            height={Math.max(
              useContentHeight({
                withToolBar: true,
                withPanelPaddingTop: true,
                withPanelPaddingBottom: true,
                withPanelHeader: true,
                withTablePagination: true,
              }) - 338,
              LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT
            )}
          />
          <Table.Pagination />
        </Table>
      </Panel>
    </>
  );
};

export default Margin;
